import { FC, JSX } from 'react';
import { Box } from '@mui/joy';
import './CreateOrEditCompany.scss';
import { CompanyType } from '@consolidocs-ui/types';
import { useCompanyForm } from './useCompanyForm';
import WizardStepper from './WizardStepper';
import WizardActions from './WizardActions';
import Step1CompanyDetails from './Step1CompanyDetails';
import Step2InfrastructureConfig from './Step2InfrastructureConfig';
import Step2ReviewSummary from './Step2ReviewSummary';

interface CompanyFormProps {
  editData?: CompanyType;
  onCancel: () => void;
  onSuccess?: () => void;
}

const CompanyForm: FC<CompanyFormProps> = ({
  editData,
  onCancel,
  onSuccess,
}): JSX.Element => {
  const form = useCompanyForm({ editData, onSuccess });

  return (
    <Box
      className="company-wizard"
      // Swallow Enter-key submits inside inputs so the API can never fire
      // implicitly — only explicit button clicks trigger transitions.
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
          e.preventDefault();
        }
      }}
    >
      {/* General edit is a single step (details + save); the stepper is shown
          while creating and when editing an enterprise org. */}
      {!form.isGeneralEdit && <WizardStepper form={form} />}

      {form.activeStep === 0 && (
        <Step1CompanyDetails
          formData={form.formData}
          errors={form.errors}
          editData={editData}
          onChange={form.handleChange}
        />
      )}

      {form.activeStep === 1 &&
        (form.isEnterprise ? (
          <Step2InfrastructureConfig
            formData={form.formData}
            errors={form.errors}
            onChange={form.handleChange}
            isEdit={form.isEdit}
            isCreated={!!form.createdOrgId}
            flags={form.provisionFlags}
            isPolling={form.isProvisioning}
            isTimedOut={form.isProvisionTimedOut}
            hasStatusError={!!form.provisionStatusError}
            isFailed={form.isProvisionFailed}
            provisioningStatus={form.provisionStatus?.provisioningStatus}
            onRetry={form.retryProvisioning}
            isRetrying={form.isRetryingProvision}
          />
        ) : (
          <Step2ReviewSummary formData={form.formData} />
        ))}

      <WizardActions form={form} onCancel={onCancel} />
    </Box>
  );
};

export default CompanyForm;
